import { useEffect, useRef, useState } from 'react';
import type { ResolvedAudio } from '@discovr/types';
import { resolveOfficialAudio } from '../api/shared';

/** Debounce before auto-verifying a pasted/typed audio URL. */
export const AUDIO_VERIFY_DEBOUNCE_MS = 600;

const AUDIO_VERIFY_ERROR = "Couldn't verify that audio. Check the link and try again.";

export type ResolveOfficialAudioStatus = 'idle' | 'loading' | 'error';

export interface UseResolveOfficialAudioResult {
  status: ResolveOfficialAudioStatus;
  errorMessage: string;
  /** Resolve one URL now. A response only applies if it's still the latest
   *  request and the component is mounted — stale/superseded results are
   *  dropped rather than repopulating state the caller has since cleared. */
  resolve: (url: string) => void;
  /** Resolve one URL without touching status/error — for restoring an
   *  already-saved value on mount, where a loading spinner or error banner
   *  would be wrong (the value is already valid; this just fetches display
   *  metadata for it). Shares `resolve`'s request-id/mounted guards, so an
   *  in-flight restore is correctly dropped if a real `resolve` supersedes
   *  it. Errors are swallowed — the caller just leaves its input as-is. */
  resolveSilently: (url: string, onSuccess: (resolved: ResolvedAudio) => void) => void;
  /** Clears status/error and invalidates any in-flight resolve, without
   *  touching caller-owned state (input text, verified value, etc). */
  reset: () => void;
}

/**
 * Shared verify-request core for the two Campaign audio inputs (create-wizard
 * `CampaignAudioSection` and edit-form `CampaignEditAudioField`): the
 * network call, loading/error status, and the request-id + mounted guards
 * that make a slow or superseded resolve a no-op. Each caller keeps its own
 * enable/disable and auto-verify-trigger logic, since those differ (the
 * create flow gates on a form toggle + "already the saved value"; the edit
 * flow gates on an add/change toggle) — only the resolve mechanics are
 * shared, since that's the part most prone to stale-closure/race bugs.
 */
export function useResolveOfficialAudio(
  onResolved: (resolved: ResolvedAudio, url: string) => void,
): UseResolveOfficialAudioResult {
  const [status, setStatus] = useState<ResolveOfficialAudioStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const reqIdRef = useRef(0);
  const aliveRef = useRef(true);
  useEffect(() => {
    // Re-arm on every mount — StrictMode (dev) runs mount → cleanup → mount,
    // and without this the ref would stay false after that cycle, causing
    // every resolve response to be dropped (stuck on "Verifying").
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const resolve = (url: string) => {
    const myId = ++reqIdRef.current;
    setStatus('loading');
    setErrorMessage('');
    resolveOfficialAudio(url)
      .then((resolved) => {
        if (!aliveRef.current || myId !== reqIdRef.current) return; // stale → ignore
        setStatus('idle');
        onResolved(resolved, url);
      })
      .catch(() => {
        if (!aliveRef.current || myId !== reqIdRef.current) return;
        setStatus('error');
        setErrorMessage(AUDIO_VERIFY_ERROR);
      });
  };

  const resolveSilently = (url: string, onSuccess: (resolved: ResolvedAudio) => void) => {
    const myId = ++reqIdRef.current;
    resolveOfficialAudio(url)
      .then((resolved) => {
        if (!aliveRef.current || myId !== reqIdRef.current) return;
        onSuccess(resolved);
      })
      .catch(() => {
        /* restore failed → leave the caller's state for a manual re-verify */
      });
  };

  const reset = () => {
    reqIdRef.current++; // invalidate any in-flight resolve
    setStatus('idle');
    setErrorMessage('');
  };

  return { status, errorMessage, resolve, resolveSilently, reset };
}
